import type { RowData } from "@tanstack/react-table";
import { flexRender, getCoreRowModel, useReactTable } from "@tanstack/react-table";
import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import intl from "react-intl-universal";
import TableColumnToggles from "./components/RowBadgeTable";
import RowSelectionToolbar from "./components/RowSelectionToolbar";
import TableBody from "./components/TableBody";
import TableHeader from "./components/TableHeader";
import TablePagination from "./components/TablePagination";
import { TableContainer, TableHorizontalScrollContainer, TableScrollContainer } from "./styled";
import { createRowSelectionColumn, ROW_SELECTION_COLUMN_ID } from "./table-row-selection-column";
import type { HeadlessTableProps } from "./types";

export function Table<TData extends RowData>(props: Readonly<HeadlessTableProps<TData>>) {
  const {
    ariaLabel,
    columns,
    data,
    status = "ready",
    getRowId,
    initialColumnPinning,
    enableRowSelection = false,
    onSelectedRowsDelete,
    pagination,
  } = props;
  const [columnVisibility, setColumnVisibility] = useState({});
  const [rowSelection, setRowSelection] = useState({});
  const copy = {
    loading: intl.get("commons.table.loading"),
    empty: intl.get("commons.table.empty"),
  } as const;

  const tableColumns = useMemo(
    () => (enableRowSelection ? [createRowSelectionColumn<TData>(), ...columns] : columns),
    [columns, enableRowSelection]
  );

  const table = useReactTable({
    columns: tableColumns,
    data,
    getRowId,
    getCoreRowModel: getCoreRowModel(),
    enableRowSelection,
    defaultColumn: { size: 164, minSize: 0 },
    state: { columnVisibility, rowSelection },
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    initialState: {
      columnPinning: {
        left: [
          ...(enableRowSelection ? [ROW_SELECTION_COLUMN_ID] : []),
          ...(initialColumnPinning?.left ?? []),
        ],
        right: initialColumnPinning?.right ?? [],
      },
    },
  });

  const selectedRows = table.getSelectedRowModel().rows.map((row) => row.original);
  const selectedRowCount = selectedRows.length;
  const shouldRenderPagination = Boolean(
    pagination && status === "ready" && pagination.totalItems > 0
  );
  const statusMessage = status === "ready" ? null : copy[status];

  useEffect(() => {
    if (!pagination) {
      return;
    }

    setRowSelection({});
  }, [pagination]);

  const handleClearSelection = () => {
    table.setRowSelection({});
  };

  const handleDeleteSelection = () => {
    onSelectedRowsDelete?.(selectedRows);
    handleClearSelection();
  };

  const selectionToolbar =
    selectedRowCount > 0
      ? createPortal(
          <RowSelectionToolbar
            selectedCount={selectedRowCount}
            onClose={handleClearSelection}
            onDelete={handleDeleteSelection}
            deleteDisabled={!onSelectedRowsDelete}
          />,
          document.body
        )
      : null;

  return (
    <>
      {selectionToolbar}
      <TableContainer>
        <TableColumnToggles table={table} />
        <TableScrollContainer aria-label={ariaLabel} $hasPagination={shouldRenderPagination}>
          <TableHorizontalScrollContainer>
            <table
              style={{ borderSpacing: 0, tableLayout: "fixed", width: "100%" }}
              aria-label={ariaLabel}
              aria-busy={status === "loading"}
            >
              <TableHeader
                table={table}
                renderHeader={(header) =>
                  flexRender(header.column.columnDef.header, header.getContext())
                }
              />
              {status === "ready" ? (
                <TableBody
                  table={table}
                  rows={table.getRowModel().rows}
                  renderCell={(cell) => flexRender(cell.column.columnDef.cell, cell.getContext())}
                />
              ) : null}
            </table>
            {statusMessage ? (
              <output style={{ display: "block", textAlign: "center", paddingTop: 24 }}>
                {statusMessage}
              </output>
            ) : null}
          </TableHorizontalScrollContainer>
        </TableScrollContainer>
        {shouldRenderPagination && pagination ? (
          <TablePagination
            pageIndex={pagination.pageIndex}
            pageSize={pagination.pageSize}
            totalItems={pagination.totalItems}
            onPaginationChange={pagination.onPaginationChange}
            pageSizeOptions={pagination.pageSizeOptions}
            visiblePageCount={pagination.visiblePageCount}
          />
        ) : null}
      </TableContainer>
    </>
  );
}
